import { Box, Tooltip, TooltipProps, tooltipClasses } from '@mui/material'
import { styled } from '@mui/material/styles'
import { ReactElement, forwardRef } from 'react'
import { COLORS } from 'styles/colors'

interface LightTooltipProps {
	oneLine?: boolean
	maxWidth?: string | number
}

const LightTooltip = styled(
	({ className, ...props }: TooltipProps & LightTooltipProps) => (
		<Tooltip {...props} classes={{ popper: className }} arrow />
	),
	{
		shouldForwardProp: prop => prop !== 'oneLine' && prop !== 'maxWidth',
	},
)(({ theme, oneLine, maxWidth = '320px' }) => ({
	[`& .${tooltipClasses.arrow}`]: {
		color: theme.palette.common.white,
	},

	[`& .${tooltipClasses.tooltip}`]: {
		backgroundColor: theme.palette.common.white,
		color: COLORS.secondaryFont,
		boxShadow: '0px 4px 16px 0px #00000014',
		fontSize: 12,
		fontWeight: 400,
		lineHeight: '16px',
		borderRadius: '8px',

		maxWidth,

		padding: '8px',

		whiteSpace: oneLine ? 'nowrap' : 'normal',
		overflow: 'hidden',
		textOverflow: oneLine ? 'ellipsis' : 'clip',
	},
}))

interface ITooltip {
	children: ReactElement
	isShowTooltip?: boolean
	oneLine?: boolean
	title: React.ReactNode
	placement: TooltipProps['placement']
	maxWidth?: string | number
	disableHoverBackground?: boolean
	offset?: [number, number]
	open?: boolean
	inline?: boolean
}

const TooltipHover = forwardRef<HTMLDivElement, ITooltip>((props, ref) => {
	const {
		children,
		title,
		placement,
		isShowTooltip = true,
		oneLine = false,
		maxWidth,
		disableHoverBackground = false,
		offset,
		open,
		inline = false,
	} = props
	const tooltipOpen = open === undefined ? isShowTooltip : open && isShowTooltip

	return (
		<LightTooltip
			placement={placement}
			title={title}
			disableHoverListener={open !== undefined || !isShowTooltip}
			disableFocusListener
			{...(open === undefined ? {} : { open: tooltipOpen })}
			oneLine={oneLine}
			maxWidth={maxWidth}
			PopperProps={{
				modifiers: [
					{
						name: 'preventOverflow',
						options: {
							boundary: 'window',
						},
					},
					...(offset
						? [
								{
									name: 'offset',
									options: {
										offset,
									},
								},
							]
						: []),
				],
			}}
		>
			<Box
				ref={ref}
				sx={{
					padding: '4px',
					display: inline ? 'inline-flex' : 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					width: 'fit-content',

					'&:hover': {
						padding: '4px',
						backgroundColor:
							isShowTooltip && !disableHoverBackground
								? COLORS.background
								: 'transparent',
						borderRadius: '8px',
					},
				}}
			>
				{children}
			</Box>
		</LightTooltip>
	)
})

TooltipHover.displayName = 'TooltipHover'

export default TooltipHover
